import React, { useMemo } from 'react'

import clsx from 'clsx'
import { CalendarEventItem } from 'components/calendarEventItem'
import { CalendarPopover } from 'components/calendarPopover'
import { Typography } from 'components/typography'
import {
	addDays,
	format,
	getDate,
	getDay,
	startOfMonth,
	startOfWeek
} from 'date-fns'
import { TypographyTypes } from 'interfaces'
import { CalendarDay, classNames } from 'utils'
import { greedyFill } from 'utils/calendarSlots/calendarSlots'

export const CalendarMonth: React.FC<{
	currentMonth: Date
	days: Array<CalendarDay>
}> = ({ currentMonth, days }): JSX.Element => {
	const firstDayOfMonth = startOfMonth(currentMonth)
	const maxQuantityElement = 4
	const firstCalendarDay = useMemo(
		() => startOfWeek(firstDayOfMonth, { weekStartsOn: 1 }),
		[currentMonth]
	)
	const weekDays = useMemo(
		() =>
			Array.from({ length: 7 }).map((_, index) =>
				format(addDays(firstCalendarDay, index), 'EEEE')
			),
		[firstCalendarDay]
	)

	const getPopoverPlacement = (
		dateString: string
	): 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' => {
		const date = new Date(dateString)
		const dayOfWeek = getDay(date)
		const dayOfMonth = getDate(date)
		if (dayOfWeek === 6) return 'left'
		if (dayOfWeek === 0 || (dayOfWeek === 1 && dayOfMonth > 25))
			return 'top-right'
		if (dayOfMonth >= 25) return 'top-left'
		return 'bottom'
	}

	return (
		<div className="shadow  lg:flex lg:flex-auto lg:flex-col overflow-auto">
			<div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs/6 font-semibold text-gray-700 lg:flex-none">
				{weekDays.map((day, index) => (
					<div key={index} className="py-2 bg-white text-[#0A5159]">
						{day.charAt(0)}
						<span className="sr-only sm:not-sr-only ">{day.slice(1)}</span>
					</div>
				))}
			</div>
			<div className="flex bg-gray-200 text-xs/6 text-gray-700 lg:flex-auto">
				<div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-5 md:gap-px">
					{days.map(day => {
						const firstDayOfMonthString = format(firstDayOfMonth, 'yyyy-MM-dd')
						const isBeforeMonthStart = day.date < firstDayOfMonthString

						const sortedDayEvents = [...day.events].sort(
							(a, b) =>
								new Date(String(a.publish_date)).getTime() -
								new Date(String(b.publish_date)).getTime()
						)

						const {
							visibleEvents: visibleDayEvents,
							hiddenEvents: hiddenDayEvents,
							visibleEffectiveSlots
						} = greedyFill(sortedDayEvents, maxQuantityElement)

						return (
							<div
								key={day.date}
								className={classNames(
									isBeforeMonthStart ? 'bg-gray-10 text-gray-500' : '',
									day.isCurrentMonth ? 'bg-white' : 'bg-gray-50 text-gray-500',
									'relative flex flex-col min-h-[230px] max-h-[240px]'
								)}
							>
								<time
									dateTime={day.date}
									className={clsx(
										day.isToday
											? 'bg-blue-primary text-white'
											: 'border-b border-1 border-gray-300',
										'flex size-6 items-center justify-center min-h-[60px] font-semibold flex-col'
									)}
								>
									<span>
										{day?.date?.split('-')?.pop()?.replace(/^0/, '') ?? ''}
									</span>
								</time>
								{day.events.length > 0 && (
									<div className="flex flex-col flex-1 gap-1 p-1 mt-1">
										{visibleDayEvents.map(event => (
											<CalendarEventItem
												key={`${event.id}-${event.status}`}
												event={event}
												quantityElement={day.events.length}
												effectiveSlots={visibleEffectiveSlots}
											/>
										))}
										{hiddenDayEvents.length > 0 && (
											<div className="mt-auto">
												<CalendarPopover
													day={hiddenDayEvents}
													maxQuantityElement={0}
													typeView="month"
													position="absolute"
													placement={getPopoverPlacement(day.date)}
												/>
											</div>
										)}
									</div>
								)}
							</div>
						)
					})}
				</div>
				<div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
					{days.map(day => (
						<button
							key={day.date}
							type="button"
							className={classNames(
								day.isCurrentMonth ? 'bg-white' : 'bg-gray-50',
								(day.isSelected || day.isToday) && 'font-semibold',
								day.isSelected && 'text-white',
								!day.isSelected && day.isToday && 'text-blue-primary',
								!day.isSelected &&
									day.isCurrentMonth &&
									!day.isToday &&
									'text-gray-900',
								!day.isSelected &&
									!day.isCurrentMonth &&
									!day.isToday &&
									'text-gray-500',
								'flex h-20 flex-col px-3 py-2 hover:bg-gray-100 focus:z-10 cursor-default'
							)}
						>
							<time
								dateTime={day.date}
								className={classNames(
									day.isSelected &&
										'flex size-6 items-center justify-center rounded-full',
									day.isSelected && day.isToday && 'bg-indigo-600',
									day.isSelected && !day.isToday && 'bg-gray-900',
									'ml-auto'
								)}
							>
								{day?.date?.split('-')?.pop()?.replace(/^0/, '') ?? ''}
							</time>
							{day.events.length > 0 && (
								<CalendarPopover
									day={day.events}
									maxQuantityElement={0}
									typeView="month"
								>
									<Typography
										title={`${day.events.length} Content item${
											day.events.length > 1 ? 's' : ''
										}`}
										type={TypographyTypes.span}
										fontLeading="10/17"
										className="text-[#0A5159] flex justify-center self-end mt-auto"
									/>
								</CalendarPopover>
							)}
						</button>
					))}
				</div>
			</div>
		</div>
	)
}
